import * as Sentry from "@sentry/react";
import { readOfficeHost } from "./office-host";
import { reportMessage, type LogAttributes } from "./sentry-reporter";
import { StepTimeline } from "./step-timeline";

export const UNANSWERED_REQUEST_REPORT_AFTER_MS = 30_000;

export const ADDIN_REQUEST_SPAN_OP = "addin.request";

export const ADDIN_REQUEST_STEP_SPAN_OP = "addin.request.step";

export type AddinRequestKind = "document" | "js_execution";

export type AddinRequestOutcome = "answered" | "not_answered";

export type DocumentRequestStep = "document_read" | "fulfill_post";

export type JsExecutionRequestStep =
    | "document_checkpoint"
    | "write_lock_wait"
    | "run_script"
    | "script_finished_observer"
    | "fulfill_post";

const REQUEST_RECEIVED = "received";

export class AddinRequestAnswerTimeline<Step extends string> {
    private readonly requestSpan: Sentry.Span;
    private readonly steps: StepTimeline<Step>;
    private describeProgress: () => LogAttributes = () => ({});
    private readonly stillUnansweredReport: ReturnType<typeof setTimeout>;

    constructor(
        private readonly kind: AddinRequestKind,
        private readonly requestId: string,
    ) {
        this.requestSpan = Sentry.startInactiveSpan({
            name: ADDIN_REQUEST_SPAN_OP,
            op: ADDIN_REQUEST_SPAN_OP,
            parentSpan: null,
            attributes: this.identityTags(),
        });
        this.steps = new StepTimeline<Step>(this.requestSpan, ADDIN_REQUEST_STEP_SPAN_OP);
        this.stillUnansweredReport = setTimeout(() => this.reportStillUnanswered(), UNANSWERED_REQUEST_REPORT_AFTER_MS);
    }

    enterStep(step: Step): Sentry.Span {
        return this.steps.enter(step);
    }

    describeProgressWith(describeProgress: () => LogAttributes): void {
        this.describeProgress = describeProgress;
    }

    settleWhen<Answer>(answer: Promise<Answer>): Promise<Answer> {
        answer.then(() => this.settle("answered"), () => this.settle("not_answered"));
        return answer;
    }

    private settle(outcome: AddinRequestOutcome): void {
        clearTimeout(this.stillUnansweredReport);
        this.steps.finish();
        this.requestSpan.setAttribute("outcome", outcome);
        this.requestSpan.end();
    }

    private reportStillUnanswered(): void {
        const requestStep = this.steps.stepInProgress() ?? REQUEST_RECEIVED;
        Sentry.withActiveSpan(this.requestSpan, () => reportMessage(
            `Word add-in has not answered a ${this.kind} request after ${UNANSWERED_REQUEST_REPORT_AFTER_MS / 1000}s`,
            {
                level: "warning",
                fingerprint: ["addin-request-unanswered", this.kind, requestStep],
                tags: { ...this.identityTags(), request_step: requestStep },
                extra: this.describeProgress(),
            },
        ));
    }

    private identityTags(): Record<string, string> {
        const officeHost = readOfficeHost();
        return {
            request_id: this.requestId,
            request_kind: this.kind,
            office_platform: officeHost.platform,
            office_version: officeHost.version,
        };
    }
}
